import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Search, ChevronDown, ChevronRight, Sparkles, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { serviceCallsAPI, autoScheduleAPI } from '@shared/apiClient';
import Dock from '../../shell/Dock';
import { useDispatchWorkspace } from './DispatchContext';
import { BACKLOG_SECTIONS, groupBacklogItems } from './constants';
import BacklogCard from './components/BacklogCard';
import AutoScheduleModal from './AutoScheduleModal';
import IncompleteDayReviewModal from './IncompleteDayReviewModal';
import { ServiceCallDetailsModal } from '../../pages/ServiceCalls';

export default function BacklogDock() {
  const {
    draggedItem,
    setDraggedItem,
    clearDrag,
    backlogFilter,
    setBacklogFilter,
    collapsedSections,
    toggleSection,
    setSelectedDetail,
  } = useDispatchWorkspace();

  const { data, isLoading } = useQuery(
    ['schedulingBacklog'],
    () => serviceCallsAPI.getSchedulingBacklog(),
    { refetchInterval: 30000 }
  );

  const { data: incompleteDaysData } = useQuery(
    ['incompleteTechDays'],
    () => autoScheduleAPI.getIncompleteDays(),
    { refetchInterval: 30000 }
  );

  const incompleteDayCount = incompleteDaysData?.data?.totalGroups || 0;

  const allItems = data?.data?.items || [];

  const filteredItems = useMemo(() => {
    if (!backlogFilter.trim()) return allItems;
    const q = backlogFilter.toLowerCase();
    return allItems.filter(
      (item) =>
        item.displayName?.toLowerCase().includes(q)
        || item.cityState?.toLowerCase().includes(q)
    );
  }, [allItems, backlogFilter]);

  const grouped = useMemo(() => groupBacklogItems(filteredItems), [filteredItems]);
  const totalCount = filteredItems.filter((i) => i.subgroup !== 'assigned_undated').length;
  const [autoScheduleOpen, setAutoScheduleOpen] = useState(false);
  const [incompleteReviewOpen, setIncompleteReviewOpen] = useState(false);
  const [openServiceCall, setOpenServiceCall] = useState(null);
  const [loadingServiceCallId, setLoadingServiceCallId] = useState(null);

  const handleOpenServiceCall = async (serviceCallId) => {
    if (!serviceCallId || loadingServiceCallId) return;
    setLoadingServiceCallId(serviceCallId);
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCallId);
      setOpenServiceCall(response.data.serviceCall);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load service call');
    } finally {
      setLoadingServiceCallId(null);
    }
  };

  return (
    <Dock
      id="backlog"
      side="left"
      title="Backlog"
      badge={totalCount}
      defaultSize={320}
      minSize={220}
      maxSize={620}
      collapsedLabel={totalCount}
      dataTourId="backlog-dock"
    >
      <div className="flex flex-col h-full">
        <div className="p-2 border-b border-surface-border space-y-2">
          <button
            type="button"
            onClick={() => setAutoScheduleOpen(true)}
            data-tour-id="auto-schedule-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Auto-Schedule
          </button>
          <button
            type="button"
            onClick={() => setIncompleteReviewOpen(true)}
            data-tour-id="incomplete-day-review-btn"
            className={clsx(
              'w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md border',
              incompleteDayCount > 0
                ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            )}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Incomplete Days
            {incompleteDayCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-600 text-white text-2xs font-semibold">
                {incompleteDayCount}
              </span>
            )}
          </button>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={backlogFilter}
              onChange={(e) => setBacklogFilter(e.target.value)}
              placeholder="Filter backlog..."
              className="input pl-8 py-1 text-xs"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-2 space-y-2">
          {isLoading ? (
            <div className="text-xs text-gray-500 text-center py-8">Loading backlog...</div>
          ) : (
            BACKLOG_SECTIONS.map((section) => {
              const items = grouped[section.id] || [];
              if (items.length === 0) return null;
              const collapsed = collapsedSections[section.id] ?? true;

              return (
                <div key={section.id} className={clsx('rounded-md border border-surface-border overflow-hidden', section.accent, 'border-l-4')}>
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-white/50"
                  >
                    <span className="text-xs font-semibold text-gray-800">{section.label}</span>
                    <span className="flex items-center gap-1">
                      <span className="text-2xs font-semibold tabular-nums text-gray-600">{items.length}</span>
                      {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </span>
                  </button>
                  {!collapsed && (
                    <div className="px-1.5 pb-1.5 space-y-1">
                      {items.map((item) => (
                        <BacklogCard
                          key={item.key || `${item.source}-${item.refId || item.serviceCallId}`}
                          item={item}
                          isDragging={draggedItem?.key === item.key}
                          onDragStart={setDraggedItem}
                          onDragEnd={clearDrag}
                          onClick={(clicked) => setSelectedDetail({ type: 'backlog', item: clicked })}
                          onOpenServiceCall={handleOpenServiceCall}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {!isLoading && totalCount === 0 && (
            <div className="text-xs text-gray-500 text-center py-8">Backlog is clear</div>
          )}
        </div>
      </div>
      <AutoScheduleModal isOpen={autoScheduleOpen} onClose={() => setAutoScheduleOpen(false)} />
      <IncompleteDayReviewModal
        isOpen={incompleteReviewOpen}
        onClose={() => setIncompleteReviewOpen(false)}
      />
      <ServiceCallDetailsModal
        serviceCall={openServiceCall}
        isOpen={!!openServiceCall}
        onClose={() => setOpenServiceCall(null)}
      />
    </Dock>
  );
}
